import { useMemo } from 'react';

import { Spacing } from '../../typings/index.ts';

const emptyObject = Object.freeze({});

export interface SpacingOptions {
  /**
   * Custom styles to merge spacing with (spacing overrides)
   */
  style?: React.CSSProperties;
  /**
   * If `true` uses margin, `false` uses padding
   * @default true
   */
  margin?: boolean;
  /**
   * The multiplier to use for the generated spacing (default `8`)
   */
  increments?: number;
}

/**
 * A hook for applying the spacing props as an inline style to nudge components easily
 * @returns {Object} spacingStyles
 * @param {{top: number, bottom: number, left: number, right: number, x: number, y: number}} spacing
 * @param {{margin: boolean, style: Object, increments: number}} [options]
 */
export default function useSpacing(
  { top, bottom, left, right, x, y }: Spacing = emptyObject,
  { margin = true, style = emptyObject, increments = 8 }: SpacingOptions = emptyObject
): Record<string, unknown> {
  const spacing = useMemo(() => {
    const styles = {} as Record<string, unknown>;

    if (y || top) {
      styles[margin ? 'marginTop' : 'paddingTop'] = ((y || top) as number) * increments;
    }

    if (y || bottom) {
      styles[margin ? 'marginBottom' : 'paddingBottom'] = ((y || bottom) as number) * increments;
    }

    if (x || left) {
      styles[margin ? 'marginLeft' : 'paddingLeft'] = ((x || left) as number) * increments;
    }

    if (x || right) {
      styles[margin ? 'marginRight' : 'paddingRight'] = ((x || right) as number) * increments;
    }

    return styles;
  }, [top, bottom, left, right, x, y, margin, increments]);
  const spaceStyles = useMemo(
    () => ({
      ...style,
      ...spacing
    }),
    [spacing, style]
  );

  return spaceStyles;
}
